// ./src/components/features/EventPartners/EventPartnersList/EventPartnersList.tsx
//
// Event partners list — table on desktop (>= md), cards below. Tap-safe, rights-
// gated, selection-ready, swipe on phones. Each row shows the partner, its agreed
// amount and paid/balance (from the money ledger, passed in), and a Payments action.

import React, { useCallback } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { STATUSES } from "@sdflc/utils";

import { EventPartner, eventPartnerDisplayName } from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { PartnerKindBadge, PartnerKindIcon } from "../PartnerKind/PartnerKind";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  CardSkeletonCheckbox,
  countCardActions,
  type CardSkeletonShape,
} from "@/components/generic";
import { recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";

export interface EventPartnersListProps {
  data: EventPartner[];
  loading?: boolean;
  /**
   * ⚠ REQUIRED — whether the FIRST read has come back. A skeleton belongs to that read only; a
   * refetch keeps the rows OR the empty state on screen and lets ContentLoadingOverlay say it is
   * reloading. `loading && items.length === 0` re-showed it on every refetch of an EMPTY list, so
   * an empty page read skeleton → "none" → skeleton → "none". Required so a caller cannot drop it.
   */
  isLoaded: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  /** eventPartnerId → amount paid (from the ledger). */
  paidByPartner: Record<string, number>;
  onEdit?: (p: EventPartner) => void;
  onDelete?: (p: EventPartner) => void;
  onManagePayments?: (p: EventPartner) => void;
  onAdd?: () => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  selection?: RowSelection;
  selectable?: boolean;
  hasQueryNarrowing?: boolean;
}

const isDisabled = (p: EventPartner): boolean => p.status === STATUSES.DISABLED;

const money = (amount?: number | null, currency?: string | null): string | null => {
  if (amount == null) {
    return null;
  }

  const value = amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return currency ? `${currency} ${value}` : value;
};

const StatusBadge: React.FC<{ p: EventPartner }> = ({ p }) => {
  const { t } = useTranslation(["common"]);

  if (!isDisabled(p)) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color="gray">
      {t("common:status.hidden", { defaultValue: "Hidden" })}
    </Badge>
  );
};

// ⚠ Delegating, not duplicating: the look of a partner kind now lives in ONE place so all three
// partner pages agree. See components/features/EventPartners/PartnerKind.
const KindBadge: React.FC<{ p: EventPartner }> = ({ p }) => <PartnerKindBadge kind={p.partnerKind} size="xs" />;

const PartnerIcon: React.FC<{ p: EventPartner; size?: number }> = ({ p, size = 34 }) => (
  <PartnerKindIcon kind={p.partnerKind} size={size} />
);

const MoneyCell: React.FC<{ p: EventPartner; paid: number }> = ({ p, paid }) => {
  const { t } = useTranslation(["partners"]);
  const agreed = p.amount ?? 0;
  const balance = agreed - paid;
  const agreedStr = money(p.amount, p.currencyCode);

  if (agreedStr == null && paid === 0) {
    return (
      <Text size="xs" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Box>
      <Text size="md" fw={600}>
        {agreedStr ?? money(0, p.currencyCode)}
      </Text>
      <Text size="xs" c="dimmed">
        {t("partners:list.paidOf", {
          defaultValue: "Paid {{paid}}",
          paid: money(paid, p.currencyCode) ?? "0",
        })}
        {balance > 0 ? ` · ${t("partners:list.due", { defaultValue: "{{due}} due", due: money(balance, p.currencyCode) })}` : ""}
      </Text>
    </Box>
  );
};

const RowActions: React.FC<{
  p: EventPartner;
  onEdit?: (r: EventPartner) => void;
  onDelete?: (r: EventPartner) => void;
  onPayments?: (r: EventPartner) => void;
  isDeleting: boolean;
}> = ({ p, onEdit, onDelete, onPayments, isDeleting }) => {
  const { t } = useTranslation(["common", "partners"]);

  const showEdit = !!onEdit && recordHasRight(p, "edit");
  const showDelete = !!onDelete && recordHasRight(p, "remove");

  return (
    <Group gap={4} wrap="nowrap">
      {onPayments && (
        <Tooltip label={t("partners:actions.payments", { defaultValue: "Payments" })} withArrow position="bottom">
          <ActionIcon variant="subtle" color="teal" size="md" onClick={() => onPayments(p)} disabled={isDeleting}>
            <AppIcon iconName="Coin" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon variant="subtle" color="blue" size="md" onClick={() => onEdit(p)} disabled={isDeleting}>
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon variant="subtle" color="red" size="md" onClick={() => onDelete(p)} loading={isDeleting} disabled={isDeleting}>
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

interface RowProps {
  p: EventPartner;
  name: string;
  paid: number;
  disabled: boolean;
  onEdit?: (r: EventPartner) => void;
  onDelete?: (r: EventPartner) => void;
  onPayments?: (r: EventPartner) => void;
  highlighted: boolean;
  isDeleting: boolean;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

const PartnerTableRow: React.FC<RowProps> = ({
  p, name, paid, disabled, onEdit, onDelete, onPayments, highlighted, isDeleting,
  selectionColumn, selectable, selected, onToggleSelect,
}) => (
  <Table.Tr
    style={{
      opacity: isDeleting ? 0.5 : disabled ? 0.6 : 1,
      backgroundColor: highlighted ? HIGHLIGHT_ROW_BG : selected ? "var(--mantine-color-blue-light)" : undefined,
      transition: "background-color 150ms ease, opacity 150ms ease",
    }}
  >
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        {selectable && (
          <Checkbox checked={selected} onChange={() => p.id && onToggleSelect?.(p.id)} disabled={isDeleting} aria-label={name} />
        )}
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <PartnerIcon p={p} />
        <Box style={{ minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            <KindBadge p={p} />
            {p.tierLabel && (
              <Badge size="xs" variant="outline" color="yellow">
                {p.tierLabel}
              </Badge>
            )}
            <StatusBadge p={p} />
          </Group>
          {p.contactName && (
            <Text size="xs" c="dimmed" lineClamp={1}>
              {p.contactName}
            </Text>
          )}
        </Box>
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 260 }}>
      <MoneyCell p={p} paid={paid} />
    </Table.Td>
    <Table.Td style={{ width: 140 }}>
      <RowActions p={p} onEdit={onEdit} onDelete={onDelete} onPayments={onPayments} isDeleting={isDeleting} />
    </Table.Td>
  </Table.Tr>
);

const PartnerTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Skeleton height={14} width="40%" maw={200} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 260 }}>
      <Skeleton height={14} width={120} />
    </Table.Td>
    <Table.Td style={{ width: 140 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const PartnerCardComponent: React.FC<CardProps> = ({
  p, name, paid, disabled, onEdit, onDelete, onPayments, highlighted, isDeleting,
  selectable, selected, selectionMode, onToggleSelect, forceClose, onForceCloseComplete, onSwipeStateChange,
}) => {
  const { t } = useTranslation(["common", "partners"]);
  const toggleSelect = () => p.id && onToggleSelect?.(p.id);

  const swipeActions = React.useMemo((): SwipeAction[] => {
    const acts: SwipeAction[] = [];

    if (onPayments) {
      acts.push({ key: "pay", icon: "Coin", label: t("partners:actions.payments", { defaultValue: "Payments" }), color: "teal", onClick: () => onPayments(p), disabled: isDeleting, secondary: true });
    }
    if (onEdit && recordHasRight(p, "edit")) {
      acts.push({ key: "edit", icon: "Edit", label: t("common:buttons.edit"), color: "blue", onClick: () => onEdit(p), disabled: isDeleting });
    }
    if (onDelete && recordHasRight(p, "remove")) {
      acts.push({ key: "delete", icon: "Trash", label: t("common:buttons.delete"), color: "red", onClick: () => onDelete(p), loading: isDeleting, disabled: isDeleting });
    }

    return acts;
  }, [onPayments, onEdit, onDelete, p, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : disabled ? 0.6 : 1}
      style={{ backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined, ...(highlighted ? HIGHLIGHT_STYLE : {}) }}
    >
      <Group gap="md" wrap="nowrap" align="center">
        {showCheckbox && (
          <Checkbox checked={selected} onChange={toggleSelect} onClick={(e) => e.stopPropagation()} disabled={isDeleting} aria-label={name} />
        )}
        <PartnerIcon p={p} size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            <StatusBadge p={p} />
          </Group>
          <Box mt={4}>
            <KindBadge p={p} />
          </Box>
          <Box mt={6}>
            <MoneyCell p={p} paid={paid} />
          </Box>
        </Box>
        {showActions && <RowActions p={p} onEdit={onEdit} onDelete={onDelete} onPayments={onPayments} isDeleting={isDeleting} />}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box hiddenFrom="sm" onClick={selectable ? toggleSelect : undefined} style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}>
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const PartnerCard = React.memo(PartnerCardComponent);

const PartnerCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="center">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={4} sizes={["md", "md", "md", "xs"]} gap={3} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

const EventPartnersListComponent: React.FC<EventPartnersListProps> = ({
  data,
  loading = false,
  isLoaded,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  paidByPartner,
  onEdit,
  onDelete,
  onManagePayments,
  onAdd,
  isRemoving = () => false,
  highlightedId,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { t } = useTranslation(["partners", "common"]);
  const { isDesktop } = useAppEnvironment();

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onManagePayments, onEdit, onDelete);
  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  const handleEdit = useCallback((p: EventPartner) => { closeAllCards(); onEdit?.(p); }, [onEdit, closeAllCards]);
  const handleDelete = useCallback((p: EventPartner) => { closeAllCards(); onDelete?.(p); }, [onDelete, closeAllCards]);
  const handlePayments = useCallback((p: EventPartner) => { closeAllCards(); onManagePayments?.(p); }, [onManagePayments, closeAllCards]);
  const handleSwipeStateChange = useCallback((id: string, isOpen: boolean) => { if (isOpen) { openCard(id); } }, [openCard]);

  // ⚠ The FIRST read only — see `isLoaded` on the props. ▶ npm run check-list-states
  const showSkeleton = !isLoaded;

  if (!showSkeleton && data.length === 0) {
    return (
      <Paper withBorder p="xl" radius="md">
        {hasQueryNarrowing ? (
          <EmptyState
            title={t("partners:emptyState.noMatchTitle", { defaultValue: "No partners match" })}
            description={t("partners:emptyState.noMatchDescription", { defaultValue: "Try a different search or clear the filters." })}
            iconName="Search"
            size="md"
          />
        ) : (
          <EmptyState
            title={t("partners:emptyState.title", { defaultValue: "No partners yet" })}
            description={t("partners:emptyState.description", { defaultValue: "Add the sponsors, partners and vendors on this event and track their payments." })}
            iconName="Award"
            actionLabel={t("partners:emptyState.actionLabel", { defaultValue: "Add partner" })}
            onAction={onAdd}
            size="md"
          />
        )}
      </Paper>
    );
  }

  const rowIsSelectable = (p: EventPartner): boolean =>
    isSelectable && ((!!onEdit && recordHasRight(p, "edit")) || (!!onDelete && recordHasRight(p, "remove")));

  const rowProps = (p: EventPartner): RowProps => ({
    p,
    name: eventPartnerDisplayName(p),
    paid: paidByPartner[p.id || ""] || 0,
    disabled: isDisabled(p),
    onEdit: onEdit ? handleEdit : undefined,
    onDelete: onDelete ? handleDelete : undefined,
    onPayments: onManagePayments ? handlePayments : undefined,
    highlighted: p.id === highlightedId,
    isDeleting: isRemoving(p.id),
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(p),
    selected: selection?.isSelected(p.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = data.filter((r) => rowIsSelectable(r)).map((r) => r.id).filter((id): id is string => !!id);
  const loadMore = <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />;

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                    />
                  </Table.Th>
                )}
                <Table.Th>{t("partners:list.columns.name", { defaultValue: "Partner" })}</Table.Th>
                <Table.Th style={{ width: 260 }}>{t("partners:list.columns.money", { defaultValue: "Money" })}</Table.Th>
                <Table.Th style={{ width: 140 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 6 }).map((_, i) => <PartnerTableRowSkeleton key={i} selectionColumn={isSelectable} />)
                : data.map((p) => <PartnerTableRow key={p.id} {...rowProps(p)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 5 }).map((_, i) => <PartnerCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />)}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Stack gap="sm">
        {data.map((p) => (
          <PartnerCard
            key={p.id}
            {...rowProps(p)}
            forceClose={shouldForceClose(p.id || "")}
            onForceCloseComplete={() => handleForceCloseComplete(p.id || "")}
            onSwipeStateChange={(isOpen) => handleSwipeStateChange(p.id || "", isOpen)}
          />
        ))}
      </Stack>
      {loadMore}
    </Stack>
  );
};

export const EventPartnersList = React.memo(EventPartnersListComponent);
